import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  taxReportJ05, type TaxReport, type TaxReportPreview,
  type TaxReportType, type TaxReportCheck,
} from '@/api/taxReportJ05';
import { orgs } from '@/api/catalogs';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import TaxReportJ05FormView from './TaxReportJ05FormView';
import DatePopover from '@/components/DatePopover';

/**
 * Редактор «Податкового розрахунку» (J0500110). Крок створення: юрособа +
 * період + тип + додатки. Далі — перегляд автозаповнення (агрегати + рядки
 * по фізособах з нарахувань ЗП) з можливістю ручної правки ключових сум і
 * вивантаження XML-пакета (основний + Д1 + 4ДФ) для подання через Кабінет ДПС.
 */

const now = new Date();
const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];
const TYPE_OPTIONS: { v: TaxReportType; l: string }[] = [
  { v: 'REPORTING', l: 'Звітний' },
  { v: 'REPORTING_NEW', l: 'Звітний новий' },
  { v: 'CORRECTING', l: 'Уточнюючий' },
];
const ALL_APPENDICES: { code: string; label: string }[] = [
  { code: 'D1', label: 'Додаток 1 (ЄСВ по працівниках)' },
  { code: '4DF', label: 'Додаток 4ДФ (ПДФО/ВЗ по працівниках)' },
  { code: 'D5', label: 'Додаток Д5 (трудові відносини: прийняття/звільнення/переведення/ЦПХ)' },
  { code: 'D2', label: 'Додаток Д2 (догляд за дитиною/особою — рядки вводяться вручну)' },
  { code: 'D3', label: 'Додаток Д3 (строкова військова служба — рядки вводяться вручну)' },
  { code: 'D6', label: 'Додаток Д6 (спецстаж — рядки вводяться вручну)' },
];

const money = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TaxReportJ05Editor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  const [orgId, setOrgId] = useState('');
  const [reportType, setReportType] = useState<TaxReportType>('REPORTING');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [apps, setApps] = useState<string[]>(['D1', '4DF']);
  const [error, setError] = useState<string | null>(null);
  const [rep, setRep] = useState<TaxReport | null>(null);
  const [preview, setPreview] = useState<TaxReportPreview | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);
  const [check, setCheck] = useState<TaxReportCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  // Iнкремент → форма-бланк перезавантажується (дата подання HFILL у формах
  // авто-походить від document_date — після зміни дати вгорі оновлюємо).
  const [formKey, setFormKey] = useState(0);

  const effOrgId = orgId || availableOrgs[0]?.id || '';
  // ФОП (наказ №243): з II кв. 2026 — окрема КВАРТАЛЬНА форма F0500111
  // (додатки лише Д1/4ДФ/Д5). Юрособи — місячна J0500111.
  const isFop = orgList.data?.items
    .find((o) => o.id === effOrgId)?.entity_type === 'FOP';

  // Режим перегляду існуючого розрахунку.
  useEffect(() => {
    if (isNew) return;
    taxReportJ05.get(id!).then((r) => {
      setRep(r);
      dpsSubmissions.list({
        source_type: 'doc.tax_reports', source_id: id!, limit: 1,
      }).then((s) => setSubStatus(s.items[0]?.status ?? null)).catch(() => {});
      return taxReportJ05.preview(id!).then(setPreview);
    }).catch(() => setError('Розрахунок не знайдено'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function create(manualOnly: boolean) {
    setError(null);
    if (!effOrgId) { setError('Оберіть організацію'); return; }
    if (!apps.length) { setError('Оберіть хоча б один додаток'); return; }
    try {
      const r = await taxReportJ05.create({
        organization_id: effOrgId, report_type: reportType,
        // ФОП (наказ №243, з II кв. 2026): КВАРТАЛЬНА форма F0500111 —
        // period_kind=QUARTER; юрособи — місячна.
        ...(isFop
          ? { period_kind: 'QUARTER', period_year: year, period_quarter: quarter }
          : { period_kind: 'MONTH', period_year: year, period_month: month }),
        appendices: isFop
          ? apps.filter((a) => ['D1', '4DF', 'D5'].includes(a)) : apps,
        manual_only: manualOnly,
      });
      const pv = await taxReportJ05.preview(r.id);
      setRep(r);
      setPreview(pv);
      navigate(`/tax-reports-j05/${r.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка створення');
    }
  }

  async function saveDocDate(dateYmd: string) {
    if (!rep || !dateYmd) return;
    try {
      // Дата подання (D_FILL/HFILL основного і додатків) — авто-походить від
      // document_date; після зміни перезавантажуємо форму-бланк (formKey).
      const iso = `${dateYmd}T00:00:00Z`;
      const upd = await taxReportJ05.patch(rep.id, { document_date: iso });
      setRep(upd);
      setCheck(null);
      setFormKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка зміни дати');
    }
  }

  async function runCheck() {
    if (!rep) return;
    setChecking(true);
    try {
      setCheck(await taxReportJ05.check(rep.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка перевірки');
    } finally {
      setChecking(false);
    }
  }

  // «Зберегти» — чернетка з'являється у Кабінеті ДПС (ідемпотентно).
  async function saveDraft() {
    if (!rep) return;
    setSaving(true);
    try {
      await taxReportJ05.saveToCabinet(rep.id);
      if (!subStatus) setSubStatus('DRAFT');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  }

  function toggleApp(code: string) {
    setApps((a) => a.includes(code) ? a.filter((x) => x !== code) : [...a, code]);
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий податковий розрахунок'
            : `Податковий розрахунок ${rep?.document_number ?? ''}`}
        </h2>
        {rep && (
          <>
            <button style={{ marginLeft: 'auto' }}
              onClick={runCheck} disabled={checking}>
              {checking ? '…' : 'перевірити'}
            </button>
            <button className="primary" onClick={saveDraft} disabled={saving}
              title="Зберегти чернетку розрахунку у Кабінеті ДПС">
              {saving ? '…' : 'зберегти'}
            </button>
          </>
        )}
        <button style={rep ? undefined : { marginLeft: 'auto' }}
          onClick={() => navigate('/dps-cabinet')}>до Кабінету</button>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {/* Крок створення (лише для нового) */}
      {isNew && (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap',
            alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: 240 }}>
              <label>Організація *</label>
              <select value={effOrgId} onChange={(e) => setOrgId(e.target.value)}>
                <option value="">—</option>
                {orgList.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ minWidth: 160 }}>
              <label>Тип розрахунку</label>
              <select value={reportType}
                onChange={(e) => setReportType(e.target.value as TaxReportType)}>
                {TYPE_OPTIONS.filter((t) => t.v !== 'CORRECTING').map((t) => (
                  <option key={t.v} value={t.v}>{t.l}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ minWidth: 200 }}>
              <label>Звітний період *{isFop ? ' (ФОП — квартальний)' : ''}</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {isFop ? (
                  <select value={quarter}
                    onChange={(e) => setQuarter(Number(e.target.value))}
                    style={{ flex: 2 }}>
                    {[1, 2, 3, 4].map((q) => (
                      <option key={q} value={q}>{['I', 'II', 'III', 'IV'][q - 1]} квартал</option>
                    ))}
                  </select>
                ) : (
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                    style={{ flex: 2 }}>
                    {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                )}
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  style={{ flex: 1 }}>
                  {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Додатки</label>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: 4 }}>
              {/* ФОП-форма (F0500111): лише Д1/4ДФ/Д5 — Д2/Д3 у F-серії
                  відсутні, Д6 поки не підтримано. */}
              {ALL_APPENDICES
                .filter((a) => !isFop || ['D1', '4DF', 'D5'].includes(a.code))
                .map((a) => (
                <label key={a.code} style={{ display: 'flex', gap: '0.4rem',
                  alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={apps.includes(a.code)}
                    onChange={() => toggleApp(a.code)} />
                  {a.label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem',
            flexWrap: 'wrap' }}>
            <button className="primary" onClick={() => create(false)}
              disabled={!effOrgId}>
              створити і заповнити з нарахувань
            </button>
            <button onClick={() => create(true)} disabled={!effOrgId}
              title="Створити порожній розрахунок — заповнено лише реквізити шапки, суми і таблиці бухгалтер вбиває вручну">
              створити без нарахувань
            </button>
          </div>
        </>
      )}

      {/* Перегляд автозаповнення */}
      {preview && rep && (
        <div style={{ marginTop: isNew ? '1rem' : 0 }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
            alignItems: 'center', marginBottom: '0.75rem' }}>
            <b>{preview.org.name}</b>
            <span style={{ color: 'var(--muted)' }}>ЄДРПОУ {preview.org.edrpou}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4,
              fontSize: '0.82rem', color: 'var(--muted)' }}
              title="Дата подання (HFILL) в основному розрахунку і всіх додатках">
              Дата подання
              <div style={{ width: 130 }}>
                <DatePopover value={rep.document_date?.slice(0, 10) ?? ''}
                  onChange={saveDocDate} />
              </div>
            </label>
          </div>

          {/* Результати перевірки */}
          {check && (
            <div style={{ marginBottom: '1rem' }}>
              {check.errors.length === 0 && check.warnings.length === 0 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0',
                  color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
                  ✓ Помилок не знайдено — розрахунок готовий до подання.
                </div>
              )}
              {check.errors.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca',
                  color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: 6,
                  marginBottom: '0.5rem' }}>
                  <b>Помилки ({check.errors.length}) — заважають поданню:</b>
                  <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                    {check.errors.map((e, i) => (
                      <li key={i} style={{ fontSize: '0.85rem' }}>
                        [{e.form === 'MAIN' ? 'Основний' : e.form}] {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {check.warnings.length > 0 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a',
                  color: '#92400e', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
                  <b>Попередження ({check.warnings.length}) — перевірте:</b>
                  <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                    {check.warnings.map((w, i) => (
                      <li key={i} style={{ fontSize: '0.85rem' }}>
                        [{w.form === 'MAIN' ? 'Основний' : w.form}] {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Статус у Кабінеті ДПС */}
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe',
            color: '#1e40af', padding: '0.5rem 0.75rem', borderRadius: 6,
            marginBottom: '1rem', display: 'flex', gap: '0.5rem',
            alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
                : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
                : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
                : subStatus === 'SENT' ? '→ відправлено у ДПС'
                : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
                : subStatus ? 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'
                : 'ще не збережено у Кабінеті ДПС — натисніть «зберегти» вгорі'}
            </span>
            <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
              onClick={() => navigate('/dps-cabinet')}>Кабінет ДПС →</a>
          </div>

          {/* Підсумки (read-only, авто з нарахувань) — компактні бейджі */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem',
            marginBottom: '0.75rem' }}>
            {([
              ['Осіб', String(preview.totals.insured_count)],
              ['Дохід', money(preview.totals.gross)],
              ['База ЄСВ', money(preview.totals.esv_base)],
              ['ЄСВ', money(preview.totals.esv)],
              ['ПДФО', money(preview.totals.pdfo)],
              ['ВЗ', money(preview.totals.wz)],
            ] as [string, string][]).map(([label, val]) => (
              <span key={label} style={{ display: 'inline-flex', gap: 5,
                alignItems: 'baseline', border: '1px solid #e2e8f0',
                borderRadius: 999, padding: '0.15rem 0.6rem',
                fontSize: '0.78rem', background: '#f8fafc' }}>
                <span style={{ color: 'var(--muted)' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{val}</span>
              </span>
            ))}
          </div>

          {/* Форми пакета як бланк: рядки з офіційними назвами, редаговані.
              key=formKey → форма перезавантажується після зміни дати подання. */}
          <TaxReportJ05FormView key={formKey} repId={rep.id} errors={check?.errors} />

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.75rem' }}>
            Форми заповнено автоматично з проведених нарахувань ЗП і кадрових
            наказів. Будь-який рядок чи клітинку можна виправити вручну —
            правка перекриває авто у XML. Пакет підпишіть і подайте через
            Кабінет ДПС — там він іде одним поданням (пов'язаний LINKED_DOCS).
          </p>
        </div>
      )}
    </div>
  );
}
